'use client';

import { POPUPS } from '@/app/components/popup/constants';
import { useAccountUm } from '@/hooks/use-account-um';
import { getPlatformBridge } from '@/logic/bridges';
import { removeSession } from '@/logic/login/session';
import { useGlobalContext } from '@/store/contexts/global-context';
import { useStore } from '@/store/store';
import { getRegionSwitchAccessClickedProps } from '@/tracking/amplitude/events/general/state-selector';

type SwitchRegionButtonProps = {
    linkText: string;
    dataTestId: string;
};

export const SwitchRegionButton = ({ linkText, dataTestId }: SwitchRegionButtonProps) => {
    const { logOut } = useAccountUm();
    const { isAndroidWrapper } = useGlobalContext();
    const queuePopup = useStore((s) => s.popup.queuePopup);
    const setOnCloseAction = useStore((s) => s.popup.setOnCloseAction);
    const setIsLoggedIn = useStore((s) => s.userContext.setIsLoggedIn);
    const sendEvent = useStore((s) => s.tracking.sendEvent);

    const handleClick = () => {
        sendEvent([{ destination: 'amplitude', eventProps: getRegionSwitchAccessClickedProps(linkText) }]);

        if (isAndroidWrapper) {
            const bridge = getPlatformBridge();
            if (bridge.isCompatible()) {
                bridge.exec('openStateSelector');
                return;
            }
        }

        setOnCloseAction(() => {
            const isLoggedIn = useStore.getState().userContext.isLoggedIn;

            if (isLoggedIn) {
                // Clear local session state synchronously before kicking off
                // logOut(), otherwise the UM subscribeSession callback fires
                // during clearSession, sees isLoggedIn=true, and calls
                // triggerLogOut() which removes the freshly-set REGION cookie.
                // logOut() is fire-and-forget: server cleanup is best-effort.
                // The LOGOUT popup handles navigation to / on dismiss.
                setIsLoggedIn(false);
                removeSession();
                logOut().catch(() => undefined);
                queuePopup({ type: POPUPS.LOGOUT });
                return;
            }
            // Logged-out users: no session to tear down and no LOGOUT popup
            // CMS data is fetched for unauthenticated contexts, so the popup
            // would render nothing. Navigate directly to the new region's
            // homepage.
            window.location.href = '/';
        });
        queuePopup({ type: POPUPS.STATE_SELECTOR, params: { source: 'region_switch' } });
    };

    return (
        <button
            type="button"
            data-test-id={dataTestId}
            className="footer-links__section__link footer-links__section__link--button"
            onClick={handleClick}>
            {linkText}
        </button>
    );
};
